import { useQuery } from '@tanstack/react-query';
import type { SellerDto } from '@telega/shared';
import { CarFront } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { ApiError, login } from '../api/client';
import { uk } from '../i18n/uk';
import { identify, track } from '../lib/analytics';
import { env } from '../lib/env';
import { openTelegramLink, rawInitData } from '../telegram';
import { Button, EmptyState, ErrorState, ListSkeleton, Page, Skeleton } from '../ui';

const SellerContext = createContext<SellerDto | null>(null);

export function useSeller(): SellerDto {
  const seller = useContext(SellerContext);
  if (!seller) throw new Error('useSeller() outside AuthProvider');
  return seller;
}

/** A plain browser (the site's address opened directly): a short hello and the way to the bot. */
export function OutsideTelegram() {
  return (
    <Page>
      <EmptyState
        icon={<CarFront className="size-12" />}
        title={uk.auth.outsideTitle}
        text={uk.auth.outsideText}
        action={
          env.botUsername ? (
            <div className="flex flex-col items-center gap-3">
              <Button
                variant="primary"
                onClick={() => openTelegramLink(`https://t.me/${env.botUsername}`)}
              >
                {uk.auth.outsideButton}
              </Button>
              <p className="text-[15px] text-hint select-all">@{env.botUsername}</p>
            </div>
          ) : undefined
        }
      />
    </Page>
  );
}

function Splash() {
  return (
    <Page>
      <div className="px-4 pt-5 pb-3">
        <Skeleton className="h-8 w-40" />
      </div>
      <ListSkeleton rows={5} />
    </Page>
  );
}

/** Logs in with Telegram initData on start and provides the seller to every screen. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const inside = rawInitData().length > 0;
  const session = useQuery({
    queryKey: ['session'],
    queryFn: login,
    enabled: inside,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 2,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const sellerId = session.data?.seller.id;
  useEffect(() => {
    if (!sellerId) return;
    identify(sellerId);
    track('app_opened');
  }, [sellerId]);

  if (!inside) return <OutsideTelegram />;
  if (session.isPending) return <Splash />;
  if (session.isError) {
    const expired = session.error instanceof ApiError && session.error.status === 401;
    return (
      <Page>
        <ErrorState
          message={expired ? uk.auth.expiredText : session.error.message}
          {...(expired ? {} : { onRetry: () => void session.refetch() })}
        />
      </Page>
    );
  }
  return <SellerContext.Provider value={session.data.seller}>{children}</SellerContext.Provider>;
}
